import { IRootScopeService, IAngularEvent, IScope, IWindowService } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { ICloningSession, IStratifiedPlanningSession, ICloningWorkerData, IPlanningSession, IPlanningSessionConflict } from "../models/models";
import { EnvironmentService } from "@gtmhub/env";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { WebWorkerService } from "@gtmhub/shared/services/webWorkerService";
import { ICollection } from "@gtmhub/models";
import { INgRedux } from "@gtmhub/state-management";
import { CustomFieldActions, ICustomField, ICustomFieldsStoreState } from "@gtmhub/customFields";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { GoalService } from "@gtmhub/goals/goal.service";
import { ISessionsStoreState } from "../redux/session-reducer";
import { HierarchyNode } from "d3";
import { StateParams, StateService } from "@uirouter/angularjs";
import { PlanningSessionsActions } from "../redux/session-actions";
import { getCurrentUserId } from "@gtmhub/users";
import { IGoal } from "@gtmhub/goals/models";
import { IStatusData, StatusBarService } from "@gtmhub/shared/components";
import { disableWebpackCdnPublicPath } from "@gtmhub/util/webpack";
import { CloneResourceService } from "@gtmhub/cloning/clone-resource.service";
import { FeatureToggleService } from "@gtmhub/featureToggle";

const bindStateToScope = (state: ICustomFieldsStoreState & ISessionsStoreState): ICloneSessionsRedux => {
  return {
    customFields: Object.values(state.customFields.map).reduce((customFields, typeCfArr) => ((customFields = [...customFields, ...typeCfArr]), customFields), []),
    customFieldsAreFetched: state.customFields.isFetched,
    allSessions: state.sessions.items,
    sessionNodes: state.sessions.tree ? state.sessions.tree.children : [],
    sessionsAreFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error || state.customFields.error,
  };
};

export interface ICloneSessionsRootScope extends IRootScopeService {
  renderDir: boolean;
}

export interface ISpawnWorkerData {
  sessionId: string;
  selectedSessionParentId: string;
  selectedSession: ISelectedCloningSession;
}
export interface ICloneSessionsRedux {
  customFields: ICustomField[];
  customFieldsAreFetched: boolean;
  sessionNodes: HierarchyNode<IPlanningSession>[];
  allSessions: IPlanningSession[];
  sessionsAreFetched: boolean;
  reduxItemsError: IUIError;
}
export interface ICloneSessionsScope extends ICloneSessionsRedux, IScope, IExtendedRootScopeService {
  hasCustomFields: boolean;
  hasChildObjectives: boolean;
  sessionTitle: string;
  selectedSession: ICloningSession;
  focusedSession: ICloningSession;
  searchSessionQuery: string;
  sessions: {
    sessionId: string;
    sessionParentId: string;
    title: string;
  }[];
  crossSessionLinkingPresent: boolean;
  originSessionGoals: IGoal[];
  selectSession(session: ICloningSession): void;
  resetSessionProgress(value: boolean): void;
  sendNotifications(): void;
  onSessionSelected(session: ICloningSession, keyReleased: boolean): void;
  clearSession(): void;
  cloneTasks(): void;
  cloneCustomFields(): void;
  clone(): void;
  close(): void;
}

export interface ISelectedCloningSession {
  sessionId: string;
  sessionParentId: string;
  cloneTasks?: boolean;
  cloneCustomFields?: boolean;
  cloneOnlyParentObjectives?: boolean;
  sendNotifications?: boolean;
  resetProgress?: boolean;
  title?: string;
  shouldAlignSession?: boolean;
}

export class CloneSessionsCtrl {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$window",
    "$ngRedux",
    "EnvironmentService",
    "UIErrorHandlingService",
    "WebWorkerService",
    "$uibModal",
    "StatusBarService",
    "CustomFieldActions",
    "PlanningSessionsActions",
    "GoalService",
    "$state",
    "$stateParams",
    "CloneResourceService",
    "FeatureToggleService",
  ];

  private planningSession: IStratifiedPlanningSession;
  private allSessions: IPlanningSession[];
  private workerData: ICloningWorkerData = null;
  private webWorker: Worker = null;

  private refactorSessionCloning = false;

  constructor(
    private $rootScope: ICloneSessionsRootScope,
    private $scope: ICloneSessionsScope,
    private $window: IWindowService,
    private $ngRedux: INgRedux,
    private env: EnvironmentService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private webWorkerService: WebWorkerService,
    private $uibModal: IModalService,
    private statusBarService: StatusBarService,
    private customFieldActions: CustomFieldActions,
    private planningSessionActions: PlanningSessionsActions,
    private goalService: GoalService,
    private $state: StateService,
    private $stateParams: StateParams,
    private cloneResourceService: CloneResourceService,
    private featureToggleService: FeatureToggleService
  ) {
    this.featureToggleService.isFeatureAvailable("refactor-session-cloning").then((flag) => {
      this.refactorSessionCloning = flag;
    });
    this.$scope.$on("sessionCreated", this.onSessionCreated);

    this.$rootScope.$on("rollback", this.rollbackClonedItems);

    this.$scope.selectedSession = null;

    this.$scope.selectSession = this.selectSession;
    this.$scope.onSessionSelected = this.onSessionSelected;

    this.$scope.clearSession = this.clearSession;
    this.$scope.resetSessionProgress = this.resetSessionProgress;

    this.$scope.cloneTasks = this.cloneTasks;
    this.$scope.cloneCustomFields = this.cloneCustomFields;
    this.$scope.sendNotifications = this.sendNotifications;

    this.$scope.clone = this.clone;
    this.$scope.close = this.close;

    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));
    this.$ngRedux.dispatch(this.planningSessionActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.sessionsAreFetched && this.$scope.customFieldsAreFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => this.init(),
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  }

  private init = (): void => {
    const { originSessionId } = this.$stateParams;
    const currentSession = this.findSessionInSessionNodes(originSessionId, this.$scope.sessionNodes);

    this.planningSession = currentSession;
    this.allSessions = this.$scope.allSessions;
    this.$scope.sessionTitle = currentSession.data.title;
    this.$scope.sessions = this.mapCloningSessions(currentSession);

    // get cfs from redux for session and show/ hide clone custom field checkbox
    this.$scope.hasCustomFields = this.$scope.customFields.some(
      (cf) => this.planningSession && this.planningSession.id !== null && cf.targetIds && cf.targetIds.includes(this.planningSession.id)
    );

    // Check if session Okrs have child objectives
    this.goalService
      .getGoals({ sessionId: currentSession.id, fields: "parentId,parentGoalSessionId" })
      .then((goals) => {
        this.$scope.hasChildObjectives = goals.some((goal) => goal.parentId !== "");
        this.$scope.originSessionGoals = goals;
      })
      .catch((error: IUIError) => {
        this.uiErrorHandlingService.handleModal({
          body: error.message,
        });
      });
  };

  private mapCloningSessions = (planningSession: IStratifiedPlanningSession): ICloningSession[] => {
    return this.allSessions
      .filter((session) => session.id !== planningSession.id && session.status !== "closed")
      .map((session) => {
        return {
          sessionId: session.id,
          sessionParentId: session.parentId,
          title: session.title,
        };
      });
  };

  private rollbackClonedItems = (): void => {
    this.workerData.rollback = true;
    this.sendWorkerMessage(this.workerData);
  };

  private selectSession = (session: ICloningSession): void => {
    const hasCrossSessionLinks = this.checkForCrossSessionGoalLinks(this.$scope.originSessionGoals, this.planningSession, session);
    this.$scope.crossSessionLinkingPresent = hasCrossSessionLinks;
    this.$scope.selectedSession = {
      ...session,
      shouldAlignSession: hasCrossSessionLinks,
    };
  };

  private onSessionSelected = (session: ICloningSession, keyReleased: boolean): void => {
    this.$scope.focusedSession = session;
    if (keyReleased) {
      this.$scope.selectSession(session);
    }
  };

  private onSessionCreated = (ev: IAngularEvent, session: IPlanningSession): void => {
    this.$scope.sessions.push({
      sessionId: session.id,
      sessionParentId: session.parentId,
      title: session.title,
    });

    this.$scope.selectSession({
      sessionId: session.id,
      sessionParentId: session.parentId,
      title: session.title,
    });
  };

  private clearSession = (): void => {
    this.$scope.selectedSession = null;
    this.$scope.searchSessionQuery = null;
    this.$scope.focusedSession = null;

    setTimeout(() => document.getElementById("selectSession").focus());
  };

  private resetSessionProgress = (value: boolean): void => {
    this.$scope.selectedSession.resetProgress = value;
  };

  private cloneTasks = (): void => {
    this.$scope.selectedSession.cloneTasks = !this.$scope.selectedSession.cloneTasks;
  };

  private cloneCustomFields = (): void => {
    this.$scope.selectedSession.cloneCustomFields = !this.$scope.selectedSession.cloneCustomFields;
  };

  private sendNotifications = (): void => {
    this.$scope.selectedSession.sendNotifications = !this.$scope.selectedSession.sendNotifications;
  };

  private clone = () => {
    if (this.refactorSessionCloning) {
      this.cloneResourceService.cloneSession({
        source: this.planningSession.id,
        target: this.$scope.selectedSession,
      });
    } else {
      const spawnWorkerData: ISpawnWorkerData = {
        sessionId: this.planningSession.id,
        selectedSession: this.$scope.selectedSession,
        selectedSessionParentId: this.planningSession.parent.id,
      };
      this.spawnWorker(spawnWorkerData);
    }
    this.close();
  };

  private close = () => {
    this.$state.go("^.^");
  };

  public spawnWorker = (spawnWorkerData: ISpawnWorkerData): void => {
    if (this.$window.Worker) {
      disableWebpackCdnPublicPath(() => {
        this.webWorker = new Worker(new URL(/* webpackChunkName: "cloning.worker" */ "../../web-workers/sessions/cloning.worker", import.meta.url));
      });
      this.workerData = {
        params: {
          includeMetrics: true,
          includeTasks: true,
          sessionId: spawnWorkerData.sessionId, // api requires this to be exactly named sessionId
        },
        headers: this.webWorkerService.createHttpHeaders({
          event: "session_cloned",
          session_id: spawnWorkerData.selectedSession.sessionId,
          parent_session_id: spawnWorkerData.selectedSessionParentId,
          clone_type: spawnWorkerData.selectedSession.resetProgress ? "reset_progress" : "keep_progress",
          clone_tasks: spawnWorkerData.selectedSession.cloneTasks ? "yes" : "no",
          notify: spawnWorkerData.selectedSession.sendNotifications ? "yes" : "no",
          cloned: "true",
        }),
        info: {
          selectedSession: spawnWorkerData.selectedSession,
          selectedSessionParentId: spawnWorkerData.selectedSessionParentId,
          currentUserId: getCurrentUserId(),
        },
        paths: {
          goals: this.env.getApiEndpoint("/goals"),
          metrics: this.env.getApiEndpoint("/metrics"),
          tasks: this.env.getApiEndpoint("/tasks"),
          tasksV2: this.env.getApiEndpointV2("/tasks"),
          session: this.env.getApiEndpoint("/sessions"),
          links: this.env.getApiEndpoint("/links"),
          customFieldsV1: this.env.getApiEndpoint("/customFields"),
          customFieldsV2: this.env.getApiEndpointV2("/customFields"),
        },
      };

      this.sendWorkerMessage(this.workerData);
      this.statusBarService.show();
      this.webWorker.addEventListener("message", this.onWebWorkerResponse);
      this.webWorker.addEventListener("error", this.onWebWorkerError);
    } else {
      this.uiErrorHandlingService.handleModal({
        body: localize("not_supported_in_browser"),
      });
    }
  };

  private openConflictsModal = (conflicts: ICollection<IPlanningSessionConflict>, session: IPlanningSession): void => {
    this.$uibModal.open({
      templateUrl: require("../views/conflicts.jade"),
      windowClass: "medium",
      controller: "PlanningSessionConflictsCtrl",
      resolve: {
        conflicts: () => conflicts,
        session: () => session,
        onSave: () => () => {
          this.statusBarService.show();
          this.sendWorkerMessage({ ...this.workerData, status: "continue" });
        },
        ignoreAndClone: () => true,
      },
    });
  };

  public sendWorkerMessage = (data: ICloningWorkerData): void => {
    this.webWorker.postMessage(data);
  };

  private onWebWorkerResponse = (message: { data: ICloningWorkerData }): void => {
    switch (message.data.status) {
      case "conflicts":
        {
          this.statusBarService.hide();
          this.openConflictsModal(message.data.conflicts, message.data.session);
        }
        break;
      case "cloning":
      case "starting":
        {
          const data: IStatusData = {
            status: "in_progress",
            action: "clone",
          };

          this.statusBarService.updateStatus(data);
        }
        break;
      case "error":
        {
          const data: IStatusData = {
            status: message.data.status,
            action: "clone",
          };

          this.statusBarService.updateStatus(data);
        }
        break;
      case "done":
        {
          const data: IStatusData = {
            status: message.data.status,
            action: "clone",
            params: {
              isSessionClone: true,
              includeItems: true,
              totalItemsCounter: message.data?.totalItemsCounter,
              targetSession: this.$scope.selectedSession,
            },
          };

          this.statusBarService.updateStatus(data);
          this.webWorker.terminate();
        }
        break;
      case "rollback_done":
        {
          this.webWorker.terminate();
        }
        break;
    }
  };

  private onWebWorkerError = (evt: ErrorEvent): void => {
    this.statusBarService.hide();

    this.uiErrorHandlingService.handleModal({
      body: evt.message,
    });
  };

  // We traverse all the session nodes array until we find a cloning session with a provided id
  // Planning session with provided id will always be in a node tree
  private findSessionInSessionNodes = (sessionId: string, sessions: HierarchyNode<IPlanningSession>[]): IStratifiedPlanningSession => {
    let planningSession: IStratifiedPlanningSession;
    let counter = 0;
    while (!planningSession || counter > sessions.length) {
      planningSession = this.findSessionNodeInTree(sessionId, sessions[counter++]);
    }
    return planningSession;
  };

  private findSessionNodeInTree(sessionId: string, sessionNode: HierarchyNode<IPlanningSession>): IStratifiedPlanningSession {
    if (sessionNode.id === sessionId) {
      return sessionNode as IStratifiedPlanningSession;
    } else if (sessionNode.children != null) {
      let result = null;
      for (let i = 0; result == null && i < sessionNode.children.length; i++) {
        result = this.findSessionNodeInTree(sessionId, sessionNode.children[i]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }
  private checkForCrossSessionGoalLinks(originSessionGoals: IGoal[], originSession: IStratifiedPlanningSession, session: ICloningSession): boolean {
    // Checks to see if there are cross session links between goals of an origin session and target session
    // cross session linking will be present if goal has a parent which is in a different session and target and origin sessions have different parents
    const isRootSession = originSession.parent.id === "root";
    const originSessionParentId = originSession.parent.id;
    if (isRootSession) {
      return false;
    }
    for (const goal of originSessionGoals) {
      const hasParentGoal = goal.parentId && goal.parentId !== "";
      if (hasParentGoal && goal.parentGoalSessionId !== originSession.id && session.sessionParentId !== originSessionParentId) {
        return true;
      }
    }
    return false;
  }
}
